import { API_BASE_URL } from "@/lib/api";

// The referral program's client side (docs/REFERRAL-PROGRAM.md).
//
// A friend lands on /r/{code}; the route handler records the visit, drops this cookie and sends
// them to the English site. From then on the banner shows, and the checkout page reads the code
// back as its coupon. The cookie name is shared with app/r/[code]/route.ts.
export const REFERRAL_COOKIE = "vellum_referral";
export const REFERRAL_COOKIE_MAX_AGE_SECONDS = 60 * 60 * 24 * 90; // 90 days, like the attribution cookie

export const REFERRAL_FRIEND_PERCENT = 10;
export const REFERRAL_REWARD_USD = 10;
export const REFERRAL_MINIMUM_USD = 30;

export const REFERRAL_FRIEND_LINE_LABEL = "Referral Discount (10% off)";

function readCookie(name: string): string | undefined {
  if (typeof document === "undefined") return undefined;
  const value = document.cookie
    .split("; ")
    .find((item) => item.startsWith(`${name}=`))
    ?.split("=")
    .slice(1)
    .join("=");
  return value ? decodeURIComponent(value) : undefined;
}

/** The referral code this browser arrived with, upper-cased, or undefined. */
export function readReferralCode(): string | undefined {
  const raw = readCookie(REFERRAL_COOKIE);
  if (!raw) return undefined;
  const code = raw.trim().toUpperCase();
  return /^[A-Z0-9]{2,12}-[A-Z0-9]{3,6}$/.test(code) ? code : undefined;
}

export function clearReferralCode(): void {
  if (typeof document === "undefined") return;
  document.cookie = `${REFERRAL_COOKIE}=; Max-Age=0; Path=/; SameSite=Lax`;
}

export type ReferralValidation = {
  valid: boolean;
  isReferralCode: boolean;
  kind: "friend" | null;
  percentOff: number;
  label: string | null;
  error: string | null;
  errorCode: "first_time_only" | "minimum" | "us_only" | "own_code" | null;
};

/** What this address has to spend, and what this checkout may actually take off. */
export type ReferralCredit = {
  balance: number;
  applicable: number;
  label: string;
};

/**
 * What a code is worth at this checkout. Email is optional: without it the first-time rule is not
 * applied yet. Never throws — a network failure reads as "not a referral code", and the backend
 * re-validates at order creation anyway.
 */
export async function validateReferralCode(
  code: string,
  opts: { email?: string; country?: string; subtotal?: number } = {},
): Promise<ReferralValidation | null> {
  const params = new URLSearchParams({ code });
  if (opts.email) params.set("email", opts.email);
  if (opts.country) params.set("country", opts.country);
  if (opts.subtotal !== undefined) params.set("subtotal", opts.subtotal.toFixed(2));
  try {
    const res = await fetch(`${API_BASE_URL}/api/referral/validate?${params.toString()}`, { cache: "no-store" });
    if (!res.ok) return null;
    return (await res.json()) as ReferralValidation;
  } catch {
    return null;
  }
}

export type ReferralShareChannel = "copy" | "copy_group" | "whatsapp" | "sms" | "link";

/** A share button was tapped. Fire-and-forget; the tap itself must never wait on us. */
export function recordReferralShare(code: string, channel: ReferralShareChannel): void {
  try {
    void fetch(`${API_BASE_URL}/api/referral/${encodeURIComponent(code)}/share`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ channel }),
      keepalive: true,
    }).catch(() => undefined);
  } catch {
    // ignore
  }
}

/** The /r/ link on the live site. Always the production host: this is what gets pasted into a chat. */
export function referralLink(code: string): string {
  return `https://vellumprintworks.com/r/${code}`;
}

// The two ready-to-send texts, verbatim from the brief. Backend copy: Services/Referral/ReferralCopy.cs.
export function shareTextShort(code: string): string {
  return `Hey! Here's the studio that made that custom print of my car. Hooked you up with 10% off if you want one for your ride: ${referralLink(code)}`;
}

export function shareTextGroup(code: string): string {
  return `Anyone wanting a proper gallery print of their car: here is the studio that made mine. They gave me a 10% link for friends: ${referralLink(code)}`;
}

export function whatsappShareUrl(text: string): string {
  return `https://wa.me/?text=${encodeURIComponent(text)}`;
}

export function smsShareUrl(text: string): string {
  // `sms:?&body=` opens Messages on iOS and Android with the text prefilled and no recipient.
  return `sms:?&body=${encodeURIComponent(text)}`;
}

/**
 * The customer's referral credit for this checkout. Answers zero rather than an error for an address
 * with none, so a shopper who has never referred anyone sees nothing and waits on nothing.
 */
export async function fetchReferralCredit(
  email: string,
  opts: { country?: string; subtotal?: number } = {},
): Promise<ReferralCredit | null> {
  const params = new URLSearchParams({ email });
  if (opts.country) params.set("country", opts.country);
  if (opts.subtotal !== undefined) params.set("subtotal", opts.subtotal.toFixed(2));
  try {
    const res = await fetch(`${API_BASE_URL}/api/referral/credit?${params.toString()}`, { cache: "no-store" });
    if (!res.ok) return null;
    return (await res.json()) as ReferralCredit;
  } catch {
    return null;
  }
}
